/**
 * PromotionCodeService: the plans-page coupon box's link to the API (#943).
 *
 * `check()` asks `POST /api/promotion-code/check` whether a code is an active
 * Stripe promotion code this company can use, and which plans it discounts.
 * The answer is a display-safe preview; checkout resolves the code again
 * server-side and Stripe applies it. The bearer token and Functions key are
 * added by the auth interceptors.
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PromotionCodeCheckResult } from '@core/config/promotions';

@Injectable({ providedIn: 'root' })
export class PromotionCodeService {
  private http = inject(HttpClient);

  /**
   * Checks a code for the plans page.
   *
   * Never errors: a refused or failed check becomes `{ valid: false, message }`
   * with a sentence the coupon box can show as is.
   *
   * @param code What the customer typed (or brought on the signup link).
   * @param billingInterval The interval the plan cards show.
   * @returns The API's preview.
   */
  check(code: string, billingInterval: 'monthly' | 'annual'): Observable<PromotionCodeCheckResult> {
    return this.http
      .post<PromotionCodeCheckResult>(`${environment.app_api_url}/api/promotion-code/check`, {
        code: code.trim(),
        billingInterval,
      })
      .pipe(
        map((result): PromotionCodeCheckResult =>
          result && typeof result === 'object' && 'valid' in result
            ? result.valid
              ? result
              : { ...result, reason: result.reason ?? 'invalid' }
            : { valid: false, message: "We couldn't check that code right now.", reason: 'unavailable' },
        ),
        catchError((error: HttpErrorResponse) =>
          of<PromotionCodeCheckResult>(
            error?.status === 429
              ? { valid: false, message: 'Too many codes tried. Wait a minute and try again.', reason: 'rate_limited' }
              : {
                  valid: false,
                  message: "We couldn't check that code right now. You can also enter it at checkout.",
                  reason: 'unavailable',
                },
          ),
        ),
      );
  }
}
